import { IconButton, MenuItem, TableRow, TextField } from "@mui/material";
import { StyledTableCell } from "../../../../../common/components/functional/simpleTable/styled";
import { Controller, useFormContext } from "react-hook-form";
import { ReactComponent as DeleteIcon } from "../../../../../common/icons/delete.svg";
import {
  CommonWorkTimeBankSettingMode,
  WorkTimeBankSettingMode,
} from "../../../../employment/employmentApi";
import { useTranslation } from "react-i18next";
import { leadingZerosDateFormat } from "../../../../../common/helpers/formatDateTime";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { useAppSelector } from "../../../../../app/hooks";
import { NumericFormat } from "react-number-format";
import { CustomInput } from "../../../../orders/editOrder/long/workAgreement/sections/components/CustomInput";
import { validateFromDate, validateToDate } from "./helpers";

type Props = {
  id: string;
  index: number;
  removeItem: (index: number) => void;
  sortList: () => void;
  canEdit: boolean;
  type: "workTimeBank" | "flexiHours";
};

export const SettingsItem = ({
  index,
  removeItem,
  sortList,
  id,
  canEdit,
  type,
}: Props) => {
  const { t } = useTranslation("talentProfile");

  const employmentDetails = useAppSelector((state) => state.employment)
    .additionalInfo.details;

  const {
    control,
    getValues,
    formState: { errors },
  } = useFormContext();

  const employmentStartDate = new Date(employmentDetails?.agreedStart);
  const employmentEndDate = employmentDetails.agreedEnd
    ? new Date(employmentDetails.agreedEnd)
    : undefined;

  const modeOptions =
    type === "workTimeBank"
      ? [
          ...Object.values(CommonWorkTimeBankSettingMode),
          ...Object.values(WorkTimeBankSettingMode),
        ]
      : Object.values(CommonWorkTimeBankSettingMode);

  return (
    <TableRow key={id}>
      <StyledTableCell>
        <Controller
          name={`workTimeBankSettings.${index}.from`}
          control={control}
          rules={{
            // Cannot be smaller than employment start date or greater than employment end date
            // and cannot be smaller than to date
            required: true,
            validate: (value) =>
              validateFromDate(
                value,
                getValues(`workTimeBankSettings.${index}.to`),
                employmentStartDate,
                employmentEndDate,
              ),
          }}
          render={({ field }) => (
            <DesktopDatePicker
              disabled={!canEdit}
              minDate={employmentStartDate}
              maxDate={employmentEndDate}
              value={field.value}
              onChange={(value: Date | null) => field.onChange(value)}
              format={leadingZerosDateFormat}
              slots={{ textField: TextField }}
              slotProps={{
                textField: {
                  onBlur: () => {
                    field.onBlur();
                    sortList();
                  },
                  error: !!(errors.workTimeBankSettings as any)?.[index]?.from,
                  size: "small",
                  fullWidth: true,
                },
              }}
            />
          )}
        />
      </StyledTableCell>
      <StyledTableCell>
        <Controller
          name={`workTimeBankSettings.${index}.to`}
          control={control}
          rules={{
            // Cannot be equal or greater than from date of next index, or smaller than from date of current index
            // and cannot be smaller than employment start date or greater than employment end date
            validate: (value) =>
              validateToDate(
                value,
                getValues(`workTimeBankSettings.${index}.from`),
                getValues(`workTimeBankSettings.${index + 1}.from`),
                employmentStartDate,
                employmentEndDate,
              ),
          }}
          render={({ field }) => (
            <DesktopDatePicker
              disabled={!canEdit}
              minDate={employmentStartDate}
              maxDate={employmentEndDate}
              value={field.value}
              onChange={(value: Date | null) => field.onChange(value)}
              format={leadingZerosDateFormat}
              slots={{ textField: TextField }}
              slotProps={{
                textField: {
                  onBlur: () => {
                    field.onBlur();
                    sortList();
                  },
                  error: !!(errors.workTimeBankSettings as any)?.[index]?.to,
                  size: "small",
                  fullWidth: true,
                },
              }}
            />
          )}
        />
      </StyledTableCell>
      <StyledTableCell>
        <Controller
          name={`workTimeBankSettings.${index}.mode`}
          control={control}
          render={({ field }) => (
            <TextField
              disabled={!canEdit}
              value={field.value}
              select
              size="small"
              fullWidth
              onChange={field.onChange}
            >
              {modeOptions.map((mode) => (
                <MenuItem key={mode} value={mode}>
                  {t(`${mode}`)}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </StyledTableCell>
      <StyledTableCell>
        <Controller
          name={`workTimeBankSettings.${index}.min`}
          control={control}
          render={({ field }) => (
            <NumericFormat
              disabled={!canEdit}
              value={field.value}
              valueIsNumericString
              onValueChange={(values) => field.onChange(values.value)}
              customInput={CustomInput}
              decimalScale={1}
              decimalSeparator=","
            />
          )}
        />
      </StyledTableCell>
      <StyledTableCell>
        <Controller
          name={`workTimeBankSettings.${index}.max`}
          control={control}
          render={({ field }) => (
            <NumericFormat
              disabled={!canEdit}
              value={field.value}
              valueIsNumericString
              onValueChange={(values) => field.onChange(values.value)}
              customInput={CustomInput}
              decimalScale={1}
              decimalSeparator=","
            />
          )}
        />
      </StyledTableCell>
      <StyledTableCell>
        <IconButton disabled={!canEdit} onClick={() => removeItem(index)}>
          <DeleteIcon style={{ opacity: canEdit ? 1 : 0.4 }} />
        </IconButton>
      </StyledTableCell>
    </TableRow>
  );
};
